import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, memo, useState, useEffect, useRef, } from "react";
import { ImageOffIcon, LoaderIcon, ScanLine } from "lucide-react";
import Markdown, { defaultUrlTransform, } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coldarkDark as codeStyle } from "react-syntax-highlighter/dist/esm/styles/prism";
import "katex/dist/katex.min.css";
import { FileAttachmentScope } from "@f2e/shared/types";
import { isEmbeddedDataImageUrl, preprocessLaTeX } from "@f2e/shared/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, } from "../../../components/base";
import { CopyButton } from "../../../components/Button";
import { MermaidDiagram } from "../../../components/Diagram";
import { AttachmentCardList } from "../../../components/Attachment";
import { useUiTranslation } from "../../../i18n";
import { ReasoningStepType } from "./types";
import { ActionBar } from "../feedback/ActionBar";
import { ReasoningStepRenderer } from "../feedback/ReasoningStepRenderer";
import { getReasoningStepTypeLabels } from "./reasoningStepLabels";
import { cn } from "../../../lib/utils";
const ATTACHMENT_PLACEHOLDER_PATTERN = /^codeer:\/\/attachment\/([0-9a-f]{8}-[0-9a-f-]{27})$/i;
const KB_UPLOAD_IMAGE_PATTERN = /^kb-upload:\/\/[1-9]\d*$/;
const MARKDOWN_IMAGE_CLASS_NAME = "max-h-96 w-auto rounded-md border border-zinc-200 dark:border-zinc-700";
const resolveEditableAttachmentId = (message, src) => {
    const placeholderMatch = src.match(ATTACHMENT_PLACEHOLDER_PATTERN);
    if (placeholderMatch) {
        const attachmentId = placeholderMatch[1];
        const attachedImage = message.attachedFiles?.find((file) => file.uuid === attachmentId);
        if (attachedImage?.scope === FileAttachmentScope.Ephemeral)
            return undefined;
        return attachmentId;
    }
    const galleryImages = (message.meta?.outputComponents ?? [])
        .filter((component) => component.kind === "image_gallery")
        .flatMap((component) => {
        const images = component.payload.images;
        return Array.isArray(images) ? images : [];
    })
        .filter((image) => typeof image === "object" &&
        image !== null &&
        typeof image.attachment_id === "string");
    const exactMatch = galleryImages.find((image) => image.placeholder_url === src || image.external_url === src);
    if (exactMatch)
        return exactMatch.attachment_id;
    const attachedImage = message.attachedFiles?.find((file) => file.type.startsWith("image/") && file.url === src);
    if (attachedImage?.scope === FileAttachmentScope.Ephemeral)
        return undefined;
    return attachedImage?.uuid;
};
/**
 * Custom schema for rehype-sanitize.
 * Extends default schema to allow:
 * - <tool> element with data-id/dataId attributes (for reasoning steps)
 * - KaTeX-generated elements with className/style (for math rendering)
 */
const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames ?? []), "tool", "img"],
    attributes: {
        ...defaultSchema.attributes,
        // Allow data-id and dataId on <tool> elements
        tool: ["data-id", "dataId"],
        img: ["src", "alt", "title", "width", "height", "loading"],
        // Allow className and style on all elements (needed for KaTeX)
        "*": [
            ...(defaultSchema.attributes?.["*"] ?? []),
            "className",
            "class",
            "style",
        ],
    },
    protocols: {
        ...defaultSchema.protocols,
        href: [...(defaultSchema.protocols?.href ?? []), "mailto"],
        src: [...(defaultSchema.protocols?.src ?? []), "data", "kb-upload"],
    },
};
const protectedImagePreviewCaches = new WeakMap();
const revokeProtectedImagePreview = (entry) => {
    if (entry.revoked || !entry.url?.startsWith("blob:"))
        return;
    entry.revoked = true;
    window.URL.revokeObjectURL?.(entry.url);
};
const createProtectedImagePreviewEntry = (loader, locator, alt) => {
    const entry = {
        state: "loading",
        refs: 0,
        cleanupVersion: 0,
        disposed: false,
        revoked: false,
        settled: Promise.resolve(),
    };
    const blobId = locator.slice("kb-upload://".length);
    const file = {
        id: locator,
        uuid: locator,
        name: alt || `knowledge-base-image-${blobId}`,
        type: "image/*",
        url: locator,
        status: "uploaded",
    };
    entry.settled = Promise.resolve()
        .then(() => loader(file))
        .then((url) => {
        if (!url.startsWith("blob:")) {
            entry.state = "error";
            return;
        }
        entry.state = "loaded";
        entry.url = url;
        if (entry.disposed)
            revokeProtectedImagePreview(entry);
    }, () => {
        entry.state = "error";
    });
    return entry;
};
const acquireProtectedImagePreview = (loader, locator, alt) => {
    let loaderCache = protectedImagePreviewCaches.get(loader);
    if (!loaderCache) {
        loaderCache = new Map();
        protectedImagePreviewCaches.set(loader, loaderCache);
    }
    let entry = loaderCache.get(locator);
    if (!entry || entry.disposed) {
        entry = createProtectedImagePreviewEntry(loader, locator, alt);
        loaderCache.set(locator, entry);
    }
    entry.refs += 1;
    entry.cleanupVersion += 1;
    return entry;
};
const releaseProtectedImagePreview = (loader, locator, entry) => {
    entry.refs = Math.max(0, entry.refs - 1);
    const cleanupVersion = ++entry.cleanupVersion;
    queueMicrotask(() => {
        if (entry.refs !== 0 || entry.cleanupVersion !== cleanupVersion)
            return;
        const loaderCache = protectedImagePreviewCaches.get(loader);
        if (loaderCache?.get(locator) === entry)
            loaderCache.delete(locator);
        entry.disposed = true;
        revokeProtectedImagePreview(entry);
    });
};
const ProtectedMarkdownImage = ({ locator, alt, loader, }) => {
    const { t } = useUiTranslation();
    const [loadState, setLoadState] = useState({ locator, state: "loading" });
    const [decodeFailedUrl, setDecodeFailedUrl] = useState(null);
    useEffect(() => {
        setDecodeFailedUrl(null);
    }, [loader, locator]);
    useEffect(() => {
        let active = true;
        const entry = acquireProtectedImagePreview(loader, locator, alt);
        const syncEntryState = () => {
            if (!active)
                return;
            setLoadState({ locator, state: entry.state, url: entry.url });
        };
        syncEntryState();
        void entry.settled.then(syncEntryState);
        return () => {
            active = false;
            releaseProtectedImagePreview(loader, locator, entry);
        };
    }, [alt, loader, locator]);
    const currentState = loadState.locator === locator
        ? loadState
        : { locator, state: "loading" };
    const loadingLabel = alt
        ? `${t("chat.imageLoading", "Loading image...")} ${alt}`
        : t("chat.imageLoading", "Loading image...");
    const unavailableLabel = alt
        ? `${t("chat.imageUnavailable", "Image unavailable.")} ${alt}`
        : t("chat.imageUnavailable", "Image unavailable.");
    if (currentState.state === "loading") {
        return (_jsx("span", { role: "status", "aria-label": loadingLabel, "aria-busy": "true", className: "inline-flex min-h-16 min-w-16 items-center justify-center rounded-md border border-zinc-200 text-muted-foreground dark:border-zinc-700", "data-protected-image-state": "loading", children: _jsx(LoaderIcon, { className: "size-4 animate-spin-slow", "aria-hidden": "true" }) }));
    }
    if (currentState.state === "error" ||
        !currentState.url ||
        decodeFailedUrl === currentState.url) {
        return (_jsxs("span", { role: "img", "aria-label": unavailableLabel, className: "inline-flex min-h-16 min-w-16 items-center justify-center rounded-md border border-zinc-200 px-3 text-sm text-muted-foreground dark:border-zinc-700", "data-protected-image-state": "error", children: [_jsx(ImageOffIcon, { className: "mr-2 size-4 shrink-0", "aria-hidden": "true" }), _jsx("span", { children: alt || t("chat.imageUnavailable", "Image unavailable.") })] }));
    }
    return (_jsx("img", { src: currentState.url, alt: alt, className: MARKDOWN_IMAGE_CLASS_NAME, "data-protected-image-state": "loaded", onError: () => setDecodeFailedUrl(currentState.url ?? null) }));
};
const MessageBlockComponent = ({ history, message, isProcessing, reasoningSteps, resolveUrl, extensions = [], customActionBar, hideSensitiveReasoningDetails = false, onHelpfulFeedback, onImproveFeedback, onFeedbackBlocked, onDownload, onPreviewLoad, onEditImage, }) => {
    const { t } = useUiTranslation();
    const stepTypeLabels = useMemo(() => getReasoningStepTypeLabels(t), [t]);
    const hasFinishedStep = reasoningSteps?.some((step) => step.type === ReasoningStepType.ConsultantFinished) ?? false;
    // Get the latest step ID (last non-finished step)
    const latestStepId = useMemo(() => {
        if (!reasoningSteps || reasoningSteps.length === 0 || hasFinishedStep) {
            return null;
        }
        const nonFinishedSteps = reasoningSteps.filter((step) => step.type !== ReasoningStepType.ConsultantFinished);
        return nonFinishedSteps[nonFinishedSteps.length - 1]?.id ?? null;
    }, [reasoningSteps, hasFinishedStep]);
    // Track which steps are expanded and which have been manually toggled
    const [expandedSteps, setExpandedSteps] = useState(new Set());
    const manuallyToggledStepsRef = useRef(new Set());
    // Auto-expand latest step or collapse all when finished
    useEffect(() => {
        if (hasFinishedStep) {
            setExpandedSteps(new Set()); // Collapse all when finished
            manuallyToggledStepsRef.current = new Set(); // Reset manual toggles for next conversation
            return;
        }
        if (!latestStepId)
            return;
        // If the latest step was manually toggled, respect user's choice
        if (manuallyToggledStepsRef.current.has(latestStepId)) {
            return;
        }
        // Auto-expand latest step only
        setExpandedSteps((prev) => {
            const next = new Set();
            // Keep manually toggled steps in their state
            prev.forEach((stepId) => {
                if (manuallyToggledStepsRef.current.has(stepId)) {
                    next.add(stepId);
                }
            });
            // Add the latest step
            next.add(latestStepId);
            return next;
        });
    }, [latestStepId, hasFinishedStep]);
    // Built-in preprocess: normalize tool tags and source tags
    const builtInPreprocess = useMemo(() => {
        let content = message.content || "";
        // Normalize <tool> tags so we do not use a bare `id` attribute.
        // React Markdown v10 + our pinned property-information version
        // can throw when handling unknown elements with an `id` attribute.
        // We rewrite `<tool id=...>` to `<tool data-id=...>` and read that in
        // the custom `tool` component instead.
        content = content.replace(/<tool\s+id=/g, "<tool data-id=");
        const latexProcessed = preprocessLaTeX(content);
        // Keep <tool> tags for custom rendering, only normalize <source> -> <em>
        return latexProcessed.replace(/<source>(.*?)<\/source>/g, "<em>$1</em>");
    }, [message.content]);
    // Create base context for extensions (without processedContent initially)
    const baseContext = useMemo(() => ({
        message,
        history,
        isProcessing,
        reasoningSteps,
    }), [message, history, isProcessing, reasoningSteps]);
    // Apply extension preprocess pipeline
    const processedContent = useMemo(() => {
        let content = builtInPreprocess;
        const ctx = {
            ...baseContext,
            processedContent: content,
        };
        for (const ext of extensions) {
            if (ext.preprocess) {
                content = ext.preprocess(content, ctx);
                // Update context for next extension
                ctx.processedContent = content;
            }
        }
        return content;
    }, [builtInPreprocess, baseContext, extensions]);
    // Execute all derive functions and collect results
    const derivedResults = useMemo(() => {
        const ctx = { ...baseContext, processedContent };
        const results = new Map();
        for (const ext of extensions) {
            if (ext.derive) {
                results.set(ext.id, ext.derive(ctx));
            }
        }
        return results;
    }, [baseContext, processedContent, extensions]);
    // Create full context with processedContent for components/footer
    const fullContext = useMemo(() => ({ ...baseContext, processedContent }), [baseContext, processedContent]);
    const isParent = (n) => !!n && typeof n === "object" && Array.isArray(n.children);
    const isToolElement = (n) => !!n &&
        typeof n === "object" &&
        n.type === "element" &&
        n.tagName === "tool";
    // Define default markdown components
    const defaultComponents = useMemo(() => ({
        pre: ({ ...props }) => _jsx("pre", { style: { padding: 0 }, ...props }),
        img: ({ src = "", alt = "" }) => {
            const resolvedSrc = src && resolveUrl ? resolveUrl(src) : src;
            if (KB_UPLOAD_IMAGE_PATTERN.test(src) &&
                onPreviewLoad &&
                resolvedSrc === src) {
                return (_jsx(ProtectedMarkdownImage, { locator: src, alt: alt, loader: onPreviewLoad }));
            }
            const attachmentId = onEditImage
                ? resolveEditableAttachmentId(message, src)
                : undefined;
            const image = (_jsx("img", { src: resolvedSrc, alt: alt, className: MARKDOWN_IMAGE_CLASS_NAME }));
            if (!attachmentId || !resolvedSrc)
                return image;
            return (_jsxs("span", { className: "group/image relative inline-block max-w-full", children: [image, _jsxs("button", { type: "button", className: "absolute right-2 bottom-2 inline-flex items-center gap-1.5 rounded-md border border-white/30 bg-black/70 px-2.5 py-1.5 text-xs font-medium text-white opacity-100 shadow-sm backdrop-blur-sm transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:opacity-0 sm:group-hover/image:opacity-100", onClick: () => onEditImage?.({
                            attachmentId,
                            src: resolvedSrc,
                            alt,
                        }), "aria-label": t("chat.imageEdit.open"), children: [_jsx(ScanLine, { className: "size-3.5", "aria-hidden": "true" }), t("chat.imageEdit.open")] })] }));
        },
        tool: (props) => {
            const children = props.children;
            // We expect data-id or dataId after the normalization above.
            const toolId = props["data-id"] ||
                props["dataId"] ||
                props["id"];
            const step = toolId
                ? reasoningSteps?.find((s) => s.id === toolId)
                : undefined;
            // Build tool context for extensions
            const toolCtx = {
                toolId,
                children,
                step,
            };
            // Check if any extension provides a custom tool render
            for (const ext of extensions) {
                if (ext.tool?.render) {
                    const derived = derivedResults.get(ext.id);
                    const customRender = ext.tool.render({ ...toolCtx, derived });
                    if (customRender !== null) {
                        return _jsx(_Fragment, { children: customRender });
                    }
                }
            }
            // Default behavior: show as span if no step, otherwise show accordion
            if (!toolId || !reasoningSteps || reasoningSteps.length === 0) {
                return _jsx("span", { children: children });
            }
            if (!step) {
                return _jsx("span", { children: children });
            }
            // Handle accordion state change (user manual toggle)
            const handleValueChange = (value) => {
                // Mark this step as manually toggled
                manuallyToggledStepsRef.current.add(step.id);
                // Update expanded state
                setExpandedSteps((prev) => {
                    const next = new Set(prev);
                    if (value) {
                        next.add(step.id);
                    }
                    else {
                        next.delete(step.id);
                    }
                    return next;
                });
            };
            const isExpanded = expandedSteps.has(step.id);
            return (_jsx("div", { className: "my-3 border-l-2 border-muted-foreground/25 pl-3 [&_h3]:m-0 [&_h3]:font-normal [&_h3]:text-sm [&_h3]:leading-tight", children: _jsx(Accordion, { type: "single", collapsible: true, value: isExpanded ? step.id : undefined, onValueChange: handleValueChange, children: _jsxs(AccordionItem, { value: step.id, className: "border-0", children: [_jsx(AccordionTrigger, { className: "my-0 py-1 hover:no-underline [&>svg]:ml-2 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-50", children: _jsx("div", { className: "flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground/80", children: _jsx("span", { children: stepTypeLabels[step.type] ?? step.content }) }) }), _jsx(AccordionContent, { className: "pb-0", children: _jsx("div", { className: "max-h-80 overflow-auto pb-0 text-sm leading-relaxed text-foreground/90", children: _jsx(ReasoningStepRenderer, { step: step, hideSensitiveReasoningDetails: hideSensitiveReasoningDetails, resolveUrl: resolveUrl }) }) })] }) }) }));
        },
        code(props) {
            const { children, className } = props;
            const match = /language-(\w+)/.exec(className || "");
            const content = String(children).replace(/\n$/, "");
            if (match && match[1] === "mermaid") {
                return (_jsx("div", { className: "my-8 mermaid-container w-full max-w-full overflow-x-auto", children: _jsx("div", { className: "w-full max-w-[1200px] mx-auto", children: _jsx(MermaidDiagram, { chart: content }) }) }));
            }
            if (match) {
                // Build code block context for extensions
                const codeBlockCtx = {
                    content,
                    language: match[1],
                    className,
                };
                // Collect metadata transformations from extensions
                let meta = {
                    displayLanguage: match[1],
                    syntaxLanguage: match[1],
                    hideHeader: false,
                };
                for (const ext of extensions) {
                    if (ext.codeBlock?.transformMeta) {
                        const partial = ext.codeBlock.transformMeta(codeBlockCtx);
                        meta = { ...meta, ...partial };
                    }
                }
                // Collect render actions from extensions
                const extensionActions = [];
                let extensionContent;
                for (const ext of extensions) {
                    if (ext.codeBlock?.renderContent) {
                        const derived = derivedResults.get(ext.id);
                        const contentOverride = ext.codeBlock.renderContent({
                            ...codeBlockCtx,
                            derived,
                        });
                        if (contentOverride !== null && contentOverride !== undefined) {
                            extensionContent = contentOverride;
                        }
                    }
                    if (ext.codeBlock?.renderActions) {
                        const derived = derivedResults.get(ext.id);
                        const action = ext.codeBlock.renderActions({
                            ...codeBlockCtx,
                            derived,
                        });
                        if (action) {
                            extensionActions.push(_jsx("span", { children: action }, ext.id));
                        }
                    }
                }
                return (_jsxs("div", { className: "relative w-full max-w-full overflow-x-auto", children: [!meta.hideHeader && (_jsx("div", { className: "flex items-center justify-between w-full px-6 py-2 pr-4 bg-zinc-800 text-zinc-100 h-9", children: meta.displayLanguage })), _jsxs("div", { className: "absolute z-10 top-0 right-0 flex gap-0", children: [_jsx(CopyButton, { content: content }), extensionActions] }), extensionContent ?? (_jsx(SyntaxHighlighter, { className: "not-prose", style: {
                                ...codeStyle,
                                'pre[class*="language-"]': {
                                    ...codeStyle['pre[class*="language-"]'],
                                    marginTop: "0",
                                },
                            }, language: meta.syntaxLanguage || match[1], useInlineStyles: true, PreTag: "div", children: content }))] }));
            }
            return _jsx("code", { className: "one-line-code", children: children });
        },
        p(props) {
            const { children, node } = props;
            const hasTool = isParent(node) && node.children?.some(isToolElement);
            // avoid <h3> tags inside <p> tags when there are <tool> tags
            if (hasTool) {
                return _jsx("div", { className: "my-2", children: children });
            }
            return _jsx("p", { className: "content", children: children });
        },
    }), [
        message,
        onEditImage,
        t,
        reasoningSteps,
        extensions,
        derivedResults,
        expandedSteps,
        stepTypeLabels,
        hideSensitiveReasoningDetails,
        resolveUrl,
        onPreviewLoad,
    ]);
    // Merge extension components (later extensions override earlier ones)
    const mergedComponents = useMemo(() => {
        let merged = defaultComponents;
        for (const ext of extensions) {
            if (ext.components) {
                const derived = derivedResults.get(ext.id);
                const extComponents = ext.components({ ...fullContext, derived });
                merged = { ...merged, ...extComponents };
            }
        }
        return merged;
    }, [defaultComponents, extensions, fullContext, derivedResults]);
    // Compute footer slots based on extension positions
    const footerSlots = useMemo(() => {
        const prepend = [];
        const append = [];
        let allReplace = true;
        let hasFooterContent = false;
        for (const ext of extensions) {
            if (ext.footer?.render) {
                const derived = derivedResults.get(ext.id);
                const content = ext.footer.render({ ...fullContext, derived });
                if (content == null || content === false)
                    continue;
                hasFooterContent = true;
                const position = ext.footer.position ?? "prepend";
                if (position !== "replace") {
                    allReplace = false;
                }
                if (position === "prepend") {
                    prepend.push(_jsx("div", { children: content }, ext.id));
                }
                else if (position === "append") {
                    append.push(_jsx("div", { children: content }, ext.id));
                }
                else if (position === "replace") {
                    // Replace content goes to prepend slot, ActionBar will be hidden
                    prepend.push(_jsx("div", { children: content }, ext.id));
                }
            }
        }
        // Only hide ActionBar if ALL extensions with footer use "replace"
        const showActionBar = !hasFooterContent || !allReplace;
        return { prepend, append, showActionBar };
    }, [extensions, fullContext, derivedResults]);
    const showEmptyProcessingIndicator = message.role === "assistant" && message.content === "" && isProcessing;
    const hasAttachments = !!(message.attachedFiles && message.attachedFiles.length > 0);
    const markdownUrlTransform = useMemo(() => (url, key, node) => {
        if (isEmbeddedDataImageUrl(url))
            return url;
        if (key === "src" &&
            node.tagName === "img" &&
            KB_UPLOAD_IMAGE_PATTERN.test(url)) {
            const resolvedUrl = resolveUrl?.(url);
            if (resolvedUrl && resolvedUrl !== url) {
                return defaultUrlTransform(resolvedUrl);
            }
            return onPreviewLoad ? url : "";
        }
        return defaultUrlTransform(url);
    }, [onPreviewLoad, resolveUrl]);
    return showEmptyProcessingIndicator ? (_jsxs("div", { className: "flex items-center gap-2 text-foreground prose dark:prose-invert w-full", children: [_jsx(LoaderIcon, { className: "w-4 h-4 animate-spin-slow" }), _jsx("span", { children: t("chat.processing", "Processing...") })] })) : (_jsxs("div", { className: "mt-10 mb-2 content text-foreground prose dark:prose-invert w-full max-w-full", children: [hasAttachments && (_jsx(AttachmentCardList, { files: message.attachedFiles, align: "left", className: "mb-4 max-w-[70%] not-prose", resolveUrl: resolveUrl, onDownload: onDownload, onPreviewLoad: onPreviewLoad })), _jsx("div", { className: cn(message.meta?.displayVariant === "destructive" &&
                    "text-destructive prose-p:text-destructive"), children: _jsx(Markdown, { rehypePlugins: [
                        rehypeRaw,
                        rehypeKatex,
                        [rehypeSanitize, sanitizeSchema],
                    ], remarkPlugins: [remarkGfm, remarkMath], components: mergedComponents, urlTransform: markdownUrlTransform, children: processedContent }) }), isProcessing && !hasFinishedStep && (_jsxs("div", { className: "mt-2 flex items-center gap-2 text-xs text-muted-foreground", children: [_jsx(LoaderIcon, { className: "w-3 h-3 animate-spin-slow" }), _jsx("span", { children: t("chat.processing", "Processing...") })] })), footerSlots.prepend.length > 0 && (_jsx("div", { className: "flex flex-col gap-2", children: footerSlots.prepend })), !isProcessing &&
                footerSlots.showActionBar &&
                (customActionBar
                    ? customActionBar
                    : history && (_jsx(ActionBar, { history: history, message: message, onHelpfulFeedback: onHelpfulFeedback, onImproveFeedback: onImproveFeedback, onFeedbackBlocked: onFeedbackBlocked }))), footerSlots.append.length > 0 && (_jsx("div", { className: "flex flex-col gap-2", children: footerSlots.append }))] }));
};
// Memoize component to avoid unnecessary re-renders during streaming
export const MessageBlock = memo(MessageBlockComponent);
//# sourceMappingURL=MessageBlock.js.map